import type { WireDataPlotComponent } from "@glide/fluent-components/dist/js/base-components";
import type { WireRenderer } from "../wire-renderer";
import React, { Suspense } from "react";
import {
    useDynamicFilter,
    useMultipleDynamicFilters,
    useSearchBar,
    WireListContainer,
} from "../wire-list-container/wire-list-container";
import type { ResponsiveSizeClass } from "@glide/wire";
import { UIStyleVariant } from "@glide/wire";
import "twin.macro";
import { WireContainer } from "../wire-container/wire-container";
import { useResizeDetector } from "react-resize-detector";
import { useResponsiveSizeClass } from "@glide/common-components";
import { TimeSeriesSelector } from "./private/time-series-selector";
import type { UseColorizationResult } from "../charts/use-colorization";
import { useColorization } from "../charts/use-colorization";
import { parseDataKey } from "../charts/custom-tooltip";
import { ComposedChart as RechartsComposedChart } from "../charts/composed-loader";

function getChartHeight(size: ResponsiveSizeClass | undefined, width: number) {
    if (width < 200) {
        return 220;
    }

    if (size === undefined) {
        return 240;
    }

    if (size === "sm") {
        return 280;
    }

    return 360;
}

function useComposedChartHeight(): { height: number; ref: React.MutableRefObject<HTMLDivElement> } {
    const size = useResponsiveSizeClass();

    // We also need the size cause in _very_ small containers the size class is not granular enough.
    const { ref, width: maybeWidth } = useResizeDetector();

    const width = maybeWidth ?? 0;
    const height = getChartHeight(size, width);

    return { height, ref };
}

type WireDataPlotWithoutFormat = Omit<WireDataPlotComponent, "format">;
type WireDataPlotRenderer = WireRenderer<WireDataPlotWithoutFormat, { isFirstComponent: boolean }>;

export const WireDataPlot: WireDataPlotRenderer = React.memo(p => {
    const {
        data,
        columnsToDisplay,
        graphTitle,
        gradientColors,
        gradientMapping,
        colorScheme,
        showYAxisLabels,
        showLegend,
        backend,
        searchBar,
        initialTimeRange,
        timeSeriesRelativeDurationState,
        timeSeriesIntervalState,
        timeSeriesEffectiveInterval,
        enableTimeFilters,
        isLoading,
        timeRangeOptions,
    } = p;

    const filterArgs = useDynamicFilter(p.dynamicFilter, backend);
    const multipleFilterProps = useMultipleDynamicFilters(p.multipleDynamicFilters, backend);
    const { searchValue, onSearchChange } = useSearchBar(searchBar, backend, undefined);
    const { height, ref } = useComposedChartHeight();

    const finalTimeRangeOptions = React.useMemo(
        () =>
            timeRangeOptions.includes(initialTimeRange) ? timeRangeOptions : [initialTimeRange, ...timeRangeOptions],
        [timeRangeOptions, initialTimeRange]
    );

    return (
        <div tw="px-4 pb-4 rounded-xl border border-border-base bg-bg-front gp-sm:px-5 gp-md:px-6">
            <WireContainer isInMultipleColumnLayout={true} tw="bg-bg-front">
                <WireListContainer
                    ref={ref}
                    title={graphTitle ?? ""}
                    titleActions={[]}
                    {...filterArgs}
                    multipleFilterProps={multipleFilterProps}
                    styleVariant={UIStyleVariant.Default}
                    searchValue={searchValue}
                    onSearchChange={onSearchChange}
                    isFirstComponent={false}
                    TimeSeriesFilterComponent={
                        enableTimeFilters ? (
                            <TimeSeriesSelector
                                timeSeriesRelativeDurationState={timeSeriesRelativeDurationState}
                                timeSeriesIntervalState={timeSeriesIntervalState}
                                timeSeriesEffectiveInterval={timeSeriesEffectiveInterval}
                                backend={backend}
                                timeRangeOptions={finalTimeRangeOptions}
                            />
                        ) : null
                    }>
                    <WireDataPlotChartWithColorization
                        data={data}
                        columnsToDisplay={columnsToDisplay}
                        showYAxisLabels={showYAxisLabels}
                        showLegend={showLegend}
                        height={height}
                        isLoading={isLoading}
                        gradientColors={gradientColors}
                        gradientMapping={gradientMapping}
                        colorScheme={colorScheme}
                    />
                </WireListContainer>
            </WireContainer>
        </div>
    );
});
export type WireDataPlotChartProps = Omit<
    WireDataPlotWithoutFormat,
    | "backend"
    | "isFirstComponent"
    | "kind"
    | "graphTitle"
    | "enableTimeFilters"
    | "initialTimeRange"
    | "timeRangeOptions"
> & {
    height: number;
};

export const WireDataPlotChartWithColorization = (p: WireDataPlotChartProps) => {
    const {
        data,
        columnsToDisplay,
        showYAxisLabels,
        showLegend,
        height,
        isLoading,
        gradientColors,
        gradientMapping,
        colorScheme,
    } = p;

    const { getColor, getDataPointColor } = useColorization({
        data,
        columnsToDisplay,
        colorScheme,
        gradientColors,
        gradientMapping,
    });

    const getCaptionFromDataKey = React.useCallback(
        (k: string): string => {
            const dk = parseDataKey(k);
            const column = columnsToDisplay.find(c => c.dataKey === dk);
            return column?.label ?? "";
        },
        [columnsToDisplay]
    );

    return (
        <>
            <div
                className={isLoading ? "loading" : ""}
                tw="[span]:(text-xs text-text-xpale) [tspan]:(text-xs text-text-xpale) [&.loading]:opacity-50">
                <Suspense fallback={<div style={{ height }} tw="w-full" />}>
                    <RechartsComposedChart
                        data={data}
                        height={height}
                        columnsToDisplay={columnsToDisplay}
                        showYAxisLabels={showYAxisLabels}
                        getColor={getColor}
                        getDataPointColor={getDataPointColor}
                        getCaptionFromDataKey={getCaptionFromDataKey}
                    />
                </Suspense>
            </div>
            {showLegend && (
                <WireDataPlotLegend
                    columnsToDisplay={columnsToDisplay}
                    getCaptionFromDataKey={getCaptionFromDataKey}
                    getColor={getColor}
                />
            )}
        </>
    );
};

export type RecomposedChartProps = Pick<WireDataPlotComponent, "columnsToDisplay" | "data" | "showYAxisLabels"> &
    Pick<UseColorizationResult, "getColor" | "getDataPointColor"> & {
        height: number;
        getCaptionFromDataKey: (dataKey: string) => string;
    };

export type WireDataPlotLegendProps = Pick<
    RecomposedChartProps,
    "columnsToDisplay" | "getCaptionFromDataKey" | "getColor"
>;

export const WireDataPlotLegend: React.FC<WireDataPlotLegendProps> = p => {
    const { columnsToDisplay, getCaptionFromDataKey, getColor } = p;
    return (
        <div tw="flex flex-wrap gap-y-2 gap-x-4 justify-center mt-4 w-full">
            {columnsToDisplay.map((col, idx) => {
                return (
                    <div key={`${col.label}-${idx}`} tw="flex items-baseline">
                        <div
                            style={{ backgroundColor: getColor(idx, 0, col.dataKey) }}
                            tw="mr-1 w-3 h-3 rounded translate-y-px"
                        />
                        <span tw="text-xs text-text-pale">{getCaptionFromDataKey(col.dataKey ?? "")}</span>
                    </div>
                );
            })}
        </div>
    );
};
